import { useState, useEffect, useRef } from 'react';
import { Program } from '~source/core/models/tv-guide';
import $ from './program-item.scss';
import { calculateDuration } from '~source/utils/calculate-duration';
import {
    calculateProgress,
    isProgramNowOnTv,
    registerCallback,
    unregisterCallback,
    startUpdater,
} from '~source/utils/program-status-updater';
import { dateToTimeNotation } from '~source/utils/beautify-timing';
import cx from '~source/utils/join-class-names';
import { Icon } from '~source/ui/components/atoms/icon/icon';
import { ProgramModal } from '~source/ui/components/molecules/program-modal/program-modal';

export interface ProgramItemProps {
    program: Program;
    isCurrent?: boolean;
    isPassed?: boolean;
    isNoContent?: boolean;
    now?: number;
    channelName: string;
}

export const ProgramItem: React.FC<ProgramItemProps> = ({
    program,
    isCurrent = false,
    isNoContent = false,
    channelName,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(isCurrent);
    const [currentProgress, setCurrentProgress] = useState(0);
    const startDateRef = useRef<Date>(new Date(program.dateStart));
    const endDateRef = useRef<Date>(new Date(program.dateEnd));

    // Update refs when program changes
    useEffect(() => {
        startDateRef.current = new Date(program.dateStart);
        endDateRef.current = new Date(program.dateEnd);
    }, [program.dateStart, program.dateEnd]);

    const updateStatus = () => {
        const startDate = startDateRef.current;
        const endDate = endDateRef.current;
        const isCurrentlyOnTv = isProgramNowOnTv(startDate, endDate);

        setCurrentStatus(isCurrentlyOnTv);

        if (isCurrentlyOnTv) {
            const progressValue = calculateProgress(startDate, endDate);
            setCurrentProgress(progressValue);
        } else {
            setCurrentProgress(0);
        }
    };

    // Register callback for real-time updates
    useEffect(() => {
        updateStatus();
        registerCallback(updateStatus);
        startUpdater();

        return () => {
            unregisterCallback(updateStatus);
        };
    }, []);

    if (isNoContent) {
        return (
            <div className={$['no-content']}>
                <span>Er zijn op deze dag op deze zender geen uitzendingen</span>
            </div>
        );
    }

    const startDate = startDateRef.current;
    const endDate = endDateRef.current;
    const duration = calculateDuration(startDate, endDate);
    const progress = currentStatus ? currentProgress : 0;
    const isCurrentlyOnTv = currentStatus;

    const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
    };

    // Determine if program has passed (only if not currently on TV)
    const programHasPassed = !isCurrentlyOnTv && endDate < new Date();

    return (
        <>
            <div
                className={cx($.root, isCurrentlyOnTv && $.current)}
                data-current={isCurrentlyOnTv}
                data-passed={programHasPassed}
                onClick={onClick}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowModal(true);
                    }
                }}
            >
                <span className={$.date}>{dateToTimeNotation(startDate)}</span>
                <h4 className={$.title}>{program.title}</h4>
                <div className={$.meta}>
                    {programHasPassed && (
                        <Icon type='replay' size={14} color='orange-500' className={$.replayIcon} />
                    )}
                    {program.live && <span className={$.live}>LIVE</span>}
                    <span className={$.duration}>{duration}</span>
                    {isCurrentlyOnTv && (
                        <div className={$.progress}>
                            <div style={{ width: `${progress}%` }} />
                        </div>
                    )}
                </div>
            </div>
            {showModal && (
                <ProgramModal
                    program={program}
                    channelName={channelName}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    );
};
